import {
  parse,
  format,
  isAfter,
  isBefore,
  isEqual,
  isPast,
  isFuture,
  isToday,
  subMonths,
  subYears,
  add,
} from "date-fns";
import { HintedValues } from "v2/infrastructure/common/types/HintedValues";

// eslint-disable-next-line no-shadow
export enum DateFormat {
  ISO_DATE = "yyyy-MM-dd",
  ISO_DATETIME = "yyyy-MM-dd'T'HH:mm:ss.SSSX",
  DATE_AND_TIME = "dd-MM-yyyy HH:mm:ss",
  BULLETED_DD_MMM_YYYY = "dd · MMM · yyyy",
  SLASHED_DD_MMM_YYYY = "dd/MM/yyyy",
  BULLETED_DD_AND_MMM = "dd · MMM",
  DASHED_DD_MM_YYYY = "dd-MM-yyyy",
}

// eslint-disable-next-line no-shadow
export enum TimeUnit {
  YEARS = "years",
  MONTHS = "months",
  WEEKS = "weeks",
  DAYS = "days",
  HOURS = "hours",
  MINUTES = "minutes",
  SECONDS = "seconds",
  MILLI_SECONDS = "milliseconds",
}

export type IsBetweenOptions = {
  timeUnit?: TimeUnit;
  fromInclusive?: boolean;
  toInclusive?: boolean;
};

const DEFAULT_FORMAT = DateFormat.ISO_DATETIME;
export default class Datetime {
  private readonly _dateInstance: Date;

  getDate() {
    return this._dateInstance;
  }

  constructor(dateInstance: Date | string | number = new Date()) {
    this._dateInstance = new Date(dateInstance);
  }

  static fromDate(input: Date) {
    return new Datetime(new Date(input));
  }

  static fromDateString(value: string, options: { format?: string } = {}) {
    const { format: optionsFormat = DEFAULT_FORMAT } = options;
    return new Datetime(parse(value, optionsFormat, new Date()));
  }

  static isStrAValidDate(value: string) {
    return !Number.isNaN(Date.parse(value));
  }

  static isAValidDate(value: Date) {
    return value instanceof Date && !Number.isNaN(Number(value));
  }

  static now() {
    return new Datetime(new Date());
  }

  clone() {
    return new Datetime(new Date(this._dateInstance));
  }

  toDatetimeString(
    optionsFormat: HintedValues<DateFormat> = DateFormat.ISO_DATE
  ) {
    return format(this._dateInstance, optionsFormat);
  }

  toLocaleDateTimeString(): string {
    return this._dateInstance.toLocaleString();
  }

  isAfter(b: Datetime) {
    return isAfter(this._dateInstance, b._dateInstance);
  }

  isBefore(b: Datetime) {
    return isBefore(this._dateInstance, b._dateInstance);
  }

  isEqual(b: Datetime) {
    return isEqual(this._dateInstance, b._dateInstance);
  }

  // eslint-disable-next-line class-methods-use-this
  isPast(b: Datetime) {
    return isPast(b._dateInstance);
  }

  static isFuture(b: Datetime) {
    return isFuture(b._dateInstance);
  }

  isToday() {
    return isToday(this._dateInstance);
  }

  subMonths(months: number) {
    return new Datetime(subMonths(this._dateInstance, months));
  }

  subYears(years: number) {
    return new Datetime(subYears(this._dateInstance, years));
  }

  add({
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
  }: {
    years?: number;
    months?: number;
    weeks?: number;
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
  }) {
    return new Datetime(
      add(this._dateInstance, {
        years,
        months,
        weeks,
        days,
        hours,
        minutes,
        seconds,
      })
    );
  }
}
